import {
  AfterViewInit,
  Directive,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  INJECTOR,
  Injector,
  input,
  Input,
  isDevMode,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { BaseDataSource, DataSourceLoader } from 'eurogard-data-source';
import { AbstractTableDataSource } from 'eurogard-data-source/table';
import { RxapFormControl } from 'eurogard-forms';
import { Mixin } from 'eurogard-mixin';
import { GenerateRandomString } from '@rxap/utilities';
import { WindowService } from 'eurogard-window-system';
import { Observable } from 'rxjs';
import { OpenTableSelectWindowMethod, TableSelectColumn } from './open-table-select-window.method';
import { ExtractTableSelectDataSourceMixin } from './extract-table-select-data-source.mixin';
import { ExtractTableSelectColumnMapMixin } from './extract-table-select-column-map.mixin';
import { ExtractTableSelectToValueMixin } from './extract-table-select-to-value.mixin';
import { ExtractTableSelectToDisplayMixin } from './extract-table-select-to-display.mixin';
import { ControlOption } from '@rxap/utilities';

export interface OpenTableSelectWindowDirective<Data extends Record<string, any>, Value = Data>
  extends ExtractTableSelectDataSourceMixin<Data>, ExtractTableSelectColumnMapMixin,
    ExtractTableSelectToValueMixin<Data, Value>, ExtractTableSelectToDisplayMixin {
}

@Mixin(ExtractTableSelectDataSourceMixin,
  ExtractTableSelectColumnMapMixin,
  ExtractTableSelectToValueMixin,
  ExtractTableSelectToDisplayMixin,
)
@Directive({
  selector: '[eurogardOpenTableSelectWindow]',
  exportAs: 'eurogardOpenTableSelectWindow',
  standalone: true,
  providers: [ OpenTableSelectWindowMethod ],
})
export class OpenTableSelectWindowDirective<Data extends Record<string, any> = Record<string, any>, Value = Data>
  implements OnChanges, OnInit, OnDestroy, AfterViewInit {

  @Input() data?: Data[] | BaseDataSource<Data[]> | AbstractTableDataSource<Data>;
  @Input() columns?: Map<string, TableSelectColumn> | Record<string, TableSelectColumn>;
  @Input() selected: Data[] = [];
  @Input() parameters?: Observable<Record<string, unknown>>;
  @Input() label?: string;
  @Input() warningMessage?: string;
  @Input() id?: string;
  @Input() compareWith?: (o1: Data, o2: Data) => boolean;

  @Output() selectedChange = new EventEmitter<Data[]>();
  @Output() addOption = new EventEmitter<ControlOption>();

  public _internalId                    = GenerateRandomString();
  public readonly multiple              = input(false);

  @HostBinding('type')
  public type                           = 'button';
  public control?: RxapFormControl;
  // required for the use mixin ExtractDatasourceMixin
  public readonly dataSourceLoader      = inject(DataSourceLoader);
  protected _hasOpenWindow              = false;
  protected readonly openMethod         = inject<OpenTableSelectWindowMethod<Data>>(OpenTableSelectWindowMethod);
  protected readonly windowService      = inject(WindowService);
  protected readonly injector: Injector = inject(INJECTOR);
  protected matButton                   = inject(MatButton, {optional: true});
  private invalidInputs                 = false;

  private _disabled = false;

  @HostBinding('disabled')
  get disabled(): boolean {
    return this._disabled || this.invalidInputs;
  }

  set disabled(value: boolean) {
    this._disabled = value;
  }

  @Input() toValue?: (value: Data) => Value | Promise<Value>;
  @Input() toDisplay?: (value: Data) => string | Promise<string>;

  public ngOnChanges() {
    this.checkInputs();
  }

  public ngOnInit() {
    this.checkInputs();
  }

  ngOnDestroy() {
    if (this.windowService.has(this._internalId)) {
      this.windowService.close(this._internalId);
    }
  }

  @HostListener('click', [ '$event' ])
  public async onClick($event: Event) {
    $event.stopPropagation();
    if (this.disabled || this._hasOpenWindow) {
      console.debug('disabled or has open window', {disabled: this.disabled, hasOpenWindow: this._hasOpenWindow});
      return;
    }
    if (!this.data || !this.columns) {
      throw new Error('FATAL: The data or columns input is not set');
    }
    setTimeout(() => {
      this.control?.disable();
    });
    const selected = await this.openMethod.call({
      windowConfig: {
        id: this._internalId,
      },
      injector: this.injector,
      data: this.data,
      columns: this.columns instanceof Map ? this.columns : new Map(Object.entries(this.columns)),
      selected: this.multiple() ? this.selected?.slice() ?? [] : [],
      title: this.label ?? 'Select value',
      warningMessage: this.warningMessage,
      compareWith: this.compareWith,
      id: this.id ?? GenerateRandomString(10),
      parameters: this.parameters,
      multiple: this.multiple(),
    });
    this.control?.enable();
    if (isDevMode()) {
      console.debug('selected', selected);
    }
    this._hasOpenWindow = false;
    if (selected !== null) {
      this.selected = selected;
      this.selectedChange.emit(this.selected);
      if (this.selected.length) {
        if (this.toValue) {
          const value = this.toValue(this.selected[0]);
          if (this.toDisplay) {
            const display = await this.toDisplay(this.selected[0]);
            this.addOption.emit({ value, display });
          } else {
            console.warn('The toDisplay method is not defined');
          }
          this.control?.setValue(value);
        } else {
          throw new Error('The toValue method is not defined');
        }
      } else {
        this.control?.reset();
      }
    }
  }

  ngAfterViewInit() {
    const hasNgControl    = !!this.injector.get(NgControl, null);
    const hasMatFormField = !!this.injector.get(MatFormField, null);
    if (hasMatFormField || hasNgControl) {
      this.matFormField = this.injector.get(MatFormField);
      this.ngControl    = this.matFormField._control.ngControl;
      this.control ??= this.ngControl?.control as RxapFormControl ?? null;
      this.data ??= this.extractTableSelectDataSource();
      this.columns ??= this.extractTableSelectColumnMap();
      this.toValue ??= this.extractTableSelectToValue() ?? (value => value as any);
      try {
        this.toDisplay ??= this.extractTableSelectToDisplay() ?? (value => value as any);
      } catch (e) {
        console.warn("Autocomplete is missing a toDisplay definition");
      }
      if (this.control) {
        this.disabled = this.control.disabled;
        this.control.registerOnDisabledChange(isDisabled => {
          this.disabled = isDisabled;
        });
      }
    }
    this.checkInputs();
  }

  public checkInputs() {
    if (!this.columns || !this.data) {
      console.warn('columns or data is not defined');
      this.invalidInputs = true;
      if (this.matButton) {
        this.matButton.disabled = this.disabled;
      }
    } else {
      if (typeof this.columns !== 'object') {
        throw new Error('Ensure that the input columns has a valid value');
      }
      if (!Array.isArray(this.data) && !(this.data instanceof BaseDataSource)) {
        throw new Error('Ensure that the input data has a valid value');
      }
      this.invalidInputs = false;
      if (this.matButton) {
        this.matButton.disabled = this.disabled;
      }
    }
  }

}
